import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Lead } from '@otw/models/leads/lead'
import { Vehicle } from '@otw/models'
import { capResults } from 'hooks/search/filterSearchItems'
import {
  fetchLeads,
  fetchMasterInventory,
  SEARCH_CACHE_STALE_MS,
  SEARCH_QUERY_KEYS,
} from 'hooks/search/searchCaches'

interface UseLeadSearchOptions {
  searchTerm: string
  enabled?: boolean
}

export interface LeadSearchResult {
  lead: Lead
  vehicle?: Vehicle
}

export function filterLeadsByTerm(
  leads: Lead[],
  vehicles: Vehicle[],
  searchTerm: string,
): LeadSearchResult[] {
  if (!searchTerm || searchTerm.trim().length < 2) {
    return []
  }

  const trimmedTerm = searchTerm.trim().toUpperCase()
  const normalizedTerm = searchTerm.trim().toLowerCase()
  const vehiclesById = new Map(vehicles.map((vehicle) => [vehicle.id, vehicle]))
  const results: LeadSearchResult[] = []
  const seenKeys = new Set<string>()

  const addResult = (lead: Lead, vehicle?: Vehicle) => {
    const key = vehicle ? `${lead.id}:${vehicle.id}` : lead.id
    if (seenKeys.has(key)) return
    seenKeys.add(key)
    results.push({ lead, vehicle })
  }

  const matchingVehicles = vehicles.filter((vehicle) => {
    const vin = vehicle.vin?.toUpperCase() || ''
    const stockNumber = vehicle.stock_number?.toUpperCase() || ''
    return vin.includes(trimmedTerm) || stockNumber.includes(trimmedTerm)
  })

  matchingVehicles.forEach((vehicle) => {
    leads.forEach((lead) => {
      if (lead.truckIds?.includes(vehicle.id)) {
        addResult(lead, vehicle)
      }
    })
  })

  leads.forEach((lead) => {
    const leadNumber = lead.leadNumber?.trim().toLowerCase()
    const customer = lead.customerName?.trim().toLowerCase() || ''
    const company = lead.company?.trim().toLowerCase() || ''
    const matchesMeta =
      leadNumber?.includes(normalizedTerm) ||
      customer.includes(normalizedTerm) ||
      company.includes(normalizedTerm)
    if (!matchesMeta) return

    if (lead.truckIds?.length) {
      lead.truckIds.forEach((truckId) => {
        addResult(lead, vehiclesById.get(truckId))
      })
    } else {
      addResult(lead)
    }
  })

  return capResults(results, 16)
}

export const useLeadSearch = ({
  searchTerm,
  enabled = true,
}: UseLeadSearchOptions) => {
  const { data: leads = [], isPending: leadsPending } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.leads,
    queryFn: fetchLeads,
    enabled,
    staleTime: SEARCH_CACHE_STALE_MS,
  })
  const { data: vehicles = [], isPending: vehiclesPending } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.inventory,
    queryFn: fetchMasterInventory,
    enabled,
    staleTime: SEARCH_CACHE_STALE_MS,
  })

  const data = useMemo(
    () => filterLeadsByTerm(leads, vehicles, searchTerm),
    [leads, vehicles, searchTerm],
  )

  return {
    data,
    isLoading: (leadsPending && leads.length === 0) || (vehiclesPending && vehicles.length === 0),
  }
}
